import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sprout, Award, Flame, Loader2, Lock, TrendingUp, Target } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api, fmt } from "../../lib/game";
import { BADGE_ICONS } from "../../lib/engage";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

export const MyProgress = ({ onSignIn }) => {
  const { user } = useAuth();
  const loggedIn = user && typeof user === "object";
  const [data, setData] = useState(null);
  const [habits, setHabits] = useState(null);

  useEffect(() => {
    if (!loggedIn) return;
    api.get("/me/badges").then(({ data }) => setData(data)).catch(() => setData(false));
    api.get("/me/habits").then(({ data }) => setHabits(data)).catch(() => setHabits(false));
  }, [loggedIn]);

  if (!loggedIn) {
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] flex flex-col items-center justify-center gap-4 px-4 text-center">
        <Award className="w-10 h-10 text-emerald-400" />
        <h1 className="font-heading text-3xl font-bold text-slate-50">Track your progress</h1>
        <p className="text-slate-400 max-w-sm">Sign in to keep a daily streak and collect skill badges as your financial decisions improve.</p>
        <Button onClick={onSignIn} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold">Sign In</Button>
      </div>
    );
  }

  if (data === null) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>;
  if (data === false) return <p className="text-center text-slate-400 py-24">Couldn't load your progress.</p>;

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-4xl font-bold text-slate-50 mb-1">My Progress</h1>
        <p className="text-slate-400 mb-6">Your streak keeps growing every day you play, and badges reward real financial skill.</p>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <div data-testid="streak-current" className="rounded-3xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.08] to-transparent bg-slate-900/60 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Sprout className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-3xl font-bold font-mono text-emerald-300">{data.current_streak}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Day streak</p>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Flame className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-3xl font-bold font-mono text-amber-300">{data.longest_streak}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Longest streak</p>
            </div>
          </div>
        </div>

        {habits && habits !== false && (habits.net_worth_series?.length > 0 || habits.follow_through_series?.length > 0) && (
          <div className="mb-8" data-testid="habit-charts">
            <h2 className="font-heading text-xl font-bold text-slate-100 mb-4">Your habits over time</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-5">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-3">
                  <TrendingUp className="w-4 h-4" /> Net worth across games
                </p>
                {habits.net_worth_series.length > 1 ? (
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                      <LineChart data={habits.net_worth_series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} formatter={(v) => fmt(v)} />
                        <Line type="monotone" dataKey="net_worth" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 py-8 text-center">Play a few games to see your net-worth trend.</p>
                )}
              </div>
              <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-5">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-teal-300 mb-3">
                  <Target className="w-4 h-4" /> Commitment follow-through
                </p>
                {habits.follow_through_series.length > 0 ? (
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                      <LineChart data={habits.follow_through_series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 10 }} unit="%" />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} formatter={(v) => `${v}%`} />
                        <Line type="monotone" dataKey="rate" stroke="#2dd4bf" strokeWidth={2.5} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 py-8 text-center">Check in on your commitments to track your follow-through.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold text-slate-100">Badges</h2>
          <span className="text-sm text-slate-400">{data.earned_count} of {data.badges.length} earned</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="badge-grid">
          {data.badges.map((b, i) => {
            const Icon = BADGE_ICONS[b.icon] || Award;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`badge-${b.id}`}
                className={`rounded-2xl border p-4 text-center ${
                  b.earned ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-slate-800 bg-slate-900/40"
                }`}
              >
                <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center relative ${
                  b.earned ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25" : "bg-slate-800"
                }`}>
                  <Icon className={`w-7 h-7 ${b.earned ? "text-slate-950" : "text-slate-600"}`} strokeWidth={2} />
                  {!b.earned && (
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-slate-500" />
                    </span>
                  )}
                </div>
                <p className={`font-heading font-bold mt-2 text-sm ${b.earned ? "text-slate-100" : "text-slate-500"}`}>{b.name}</p>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">{b.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
